import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import supabase from '../../../lib/supabaseClient';
import PageHeader from '../PageHeader';
import SearchBar from '../SearchBar';
import BulkActions from '../BulkActions';
import MoverTable from './MoverTable';
import MoverDetailsModal from './MoverDetailsModal';
import CompanyInfoModal from './CompanyInfoModal';
import AddLeadsModal from './AddLeadsModal';
import EditMoverModal from './EditMoverModal';
import { getPendingUpdates, subscribe, resolveUpdate } from '../../../services/updatesStore';
import { ExportButton, ExportModal } from '../export';
import { API_BASE_URL } from '../../../config/api';
import { 
  exportToCSV, 
  exportToJSON, 
  exportToExcel, 
  exportToPDF, 
  prepareExportData,
  generateFilename 
} from '../../../services/exportService';

const ADMIN_API_BASE = `${API_BASE_URL}/api/admin`;
const SUSPENSION_CACHE_KEY = 'adminMoverSuspensionStatus';

const readSuspensionCache = () => {
  if (typeof window === 'undefined') return {};

  try {
    const value = localStorage.getItem(SUSPENSION_CACHE_KEY);
    if (!value) return {};

    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeSuspensionCache = (cache) => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(SUSPENSION_CACHE_KEY, JSON.stringify(cache));
  } catch {
  }
};

const MoverManagement = () => {
  const [movers, setMovers] = useState([]);

  const [activeTab, setActiveTab] = useState('new'); // 'new' | 'updates'
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedMovers, setSelectedMovers] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAddLeadsModal, setShowAddLeadsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMover, setSelectedMover] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [suspendLoading, setSuspendLoading] = useState(false);
  const [suspendError, setSuspendError] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Pending updates from movers (shared store)
  const [pendingUpdates, setPendingUpdates] = useState(getPendingUpdates());

  // Fetch movers from Supabase
  useEffect(() => {
    fetchMovers();
    
    // Auto-refresh every 5 minutes
    const intervalId = setInterval(() => {
      fetchMovers();
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(intervalId);
  }, []);

  const fetchMovers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('movers_data')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch insurance and license data for all movers
      const moverIds = (data || []).map(m => m.id);
      
      const { data: insuranceData, error: insuranceError } = await supabase
        .from('mover_insurance')
        .select('*')
        .in('mover_id', moverIds)
        .order('created_at', { ascending: false });

      if (insuranceError) {
      }

      const { data: licenseData, error: licenseError } = await supabase
        .from('mover_licenses')
        .select('*')
        .in('mover_id', moverIds)
        .order('created_at', { ascending: false });

      if (licenseError) {
      }

      // Fetch reviews to calculate average rating
      const { data: reviewsData } = await supabase
        .from('mover_reviews')
        .select('mover_id, rating')
        .in('mover_id', moverIds);

      // Fetch ban status from admin API
      let authStatusMap = {};
      const suspensionCache = readSuspensionCache();

      Object.entries(suspensionCache).forEach(([email, banned]) => {
        authStatusMap[email] = {
          banned: Boolean(banned),
          banned_until: null
        };
      });
      
      try {
        const emails = (data || []).map(m => m.email);
        const response = await fetch(`${ADMIN_API_BASE}/get-mover-ban-status`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ emails })
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.banStatus) {
            authStatusMap = { ...authStatusMap, ...result.banStatus };

            const updatedCache = { ...suspensionCache };
            Object.entries(result.banStatus).forEach(([email, status]) => {
              updatedCache[email] = Boolean(status?.banned);
            });
            writeSuspensionCache(updatedCache);
          }
        } else {
        }
      } catch (error) {
      }

      // Create lookup maps
      const insuranceMap = {};
      (insuranceData || []).forEach((ins) => {
        if (!insuranceMap[ins.mover_id]) {
          insuranceMap[ins.mover_id] = [];
        }
        insuranceMap[ins.mover_id].push(ins);
      });

      const licenseMap = {};
      (licenseData || []).forEach(lic => {
        if (!licenseMap[lic.mover_id]) {
          licenseMap[lic.mover_id] = [];
        }
        licenseMap[lic.mover_id].push(lic);
      });

      // Calculate average ratings from reviews
      const ratingsMap = {};
      (reviewsData || []).forEach(review => {
        if (!ratingsMap[review.mover_id]) {
          ratingsMap[review.mover_id] = { total: 0, count: 0 };
        }
        ratingsMap[review.mover_id].total += review.rating;
        ratingsMap[review.mover_id].count += 1;
      });

      // Transform data to match the expected format
      const transformedMovers = (data || []).map((mover) => {
        const insurances = insuranceMap[mover.id] || [];
        const licenses = licenseMap[mover.id] || [];

        const approvedInsurances = insurances.filter((ins) => ins.status === 'approved');
        const approvedLicenses = licenses.filter((lic) => lic.status === 'approved');

        const latestInsurance = insurances[0] || null;
        const latestApprovedInsurance = approvedInsurances[0] || null;
        const insuranceForSummary = latestApprovedInsurance || latestInsurance;

        const normalizeLicenseType = (type) => String(type || '').trim().toLowerCase();
        const businessLicense = approvedLicenses.find((license) => normalizeLicenseType(license.license_type) === 'business license');
        const dotLicense = approvedLicenses.find((license) => {
          const type = normalizeLicenseType(license.license_type);
          return type === 'dot' || type === 'usdot number' || type === 'us dot';
        });
        const mcLicense = approvedLicenses.find((license) => {
          const type = normalizeLicenseType(license.license_type);
          return type === 'mc' || type === 'motor carrier (mc) number';
        });
        
        // Calculate average rating
        const ratingData = ratingsMap[mover.id];
        const averageRating = ratingData 
          ? Math.round((ratingData.total / ratingData.count) * 10) / 10 
          : 0;

        // Check auth ban status
        const authStatus = authStatusMap[mover.email];
        const isBanned = authStatus?.banned || false;

        return {
          id: mover.id,
          name: mover.company_name,
          location: `${mover.city || ''}, ${mover.state || ''}`.trim().replace(/^,|,$/g, '') || 'N/A',
          status: isBanned ? 'suspended' : 'active',
          plan: mover.subscription_plan || 'Not Assigned',
          planExpiry: 'N/A',
          rating: averageRating,
          leads: 0,
          total_leads: mover.total_leads || 0,
          consumed_leads: mover.consumed_leads || 0,
          conversionRate: 0,
          revenue: 0,
          insuranceStatus: latestApprovedInsurance ? 'valid' : insuranceForSummary?.status || 'pending',
          insuranceExpiry: insuranceForSummary?.valid_to || 'N/A',
          email: mover.email,
          phone: mover.phone,
          website: mover.website_link || '',
          address: mover.business_address || '',
          city: mover.city || '',
          state: mover.state || '',
          zip_code: mover.zip_code || '',
          businessLicense: businessLicense?.license_number || 'N/A',
          insuranceNumber: insuranceForSummary?.policy_number || 'N/A',
          insuranceProvider: insuranceForSummary?.insurance_provider || 'N/A',
          coverageAmount: insuranceForSummary?.coverage_amount || 'N/A',
          dotNumber: dotLicense?.license_number || 'N/A',
          mcNumber: mcLicense?.license_number || 'N/A',
          approvedInsurances: approvedInsurances,
          approvedLicenses: approvedLicenses,
          yearsInBusiness: mover.years_in_business || '',
          description: mover.company_description || '',
          years_in_business: mover.years_in_business || '',
          number_of_employees: mover.number_of_employees || '',
          company_logo: mover.company_logo || null,
          documents: {
            dot: dotLicense?.status || 'missing',
            mc: mcLicense?.status || 'missing',
            insurance: latestApprovedInsurance ? 'valid' : insuranceForSummary?.status || 'pending',
            w9: 'missing'
          }
        };
      });

      setMovers(transformedMovers);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsub = subscribe((list) => setPendingUpdates(list));
    return unsub;
  }, []);

  const filteredMovers = movers.filter(mover => {
    const matchesSearch = 
      mover.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      mover.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      mover.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || mover.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleViewDetails = (mover) => {
    setSelectedMover(mover);
    setShowDetailsModal(true);
  };

  const handleEdit = (mover) => {
    setSelectedMover(mover);
    setShowEditModal(true);
  };

  const handleSaveEdit = async (moverId, formData) => {
    try {
      // Map form field names to database column names
      const updateData = {
        company_name: formData.name,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        business_address: formData.address,
        years_in_business: formData.years_in_business,
        number_of_employees: formData.number_of_employees,
        company_description: formData.description,
        website_link: formData.website,
      };

      // Include company_logo if it was provided (updated or removed)
      if ('company_logo' in formData) {
        updateData.company_logo = formData.company_logo;
      }

      const { error } = await supabase
        .from('movers_data')
        .update(updateData)
        .eq('id', moverId);

      if (error) throw error;

      // Refresh movers data to get updated information
      await fetchMovers();

      toast.success('Mover details updated successfully!');
    } catch (error) {
      throw new Error('Failed to update mover details');
    }
  };

  const handleSuspend = (mover) => {
    setSuspendError('');
    setSuspendTarget(mover);
  };

  const closeSuspendModal = () => {
    if (suspendLoading) return;
    setSuspendError('');
    setSuspendTarget(null);
  };

  const confirmSuspendAction = async () => {
    if (!suspendTarget) return;

    const mover = suspendTarget;
    const isSuspended = mover.status === 'suspended';
    const action = isSuspended ? 'reactivate' : 'suspend';

    setSuspendLoading(true);
    setSuspendError('');

    try {
      // Call admin API to ban/unban the user
      const endpoint = isSuspended
        ? `${ADMIN_API_BASE}/unsuspend-mover`
        : `${ADMIN_API_BASE}/suspend-mover`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: mover.email })
      });

      const result = await response.json();
      
      if (!response.ok || !result.success) {
        throw new Error(result.error || result.details || 'Failed to update user status');
      }

      // Also ban/unban team members
      const { data: teamMembers } = await supabase
        .from('team_members')
        .select('email')
        .eq('mover_id', mover.id);

      if (teamMembers && teamMembers.length > 0) {
        const teamEndpoint = isSuspended
          ? `${ADMIN_API_BASE}/unsuspend-mover`
          : `${ADMIN_API_BASE}/suspend-mover`;
        for (const teamMember of teamMembers) {
          await fetch(teamEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email: teamMember.email })
          });
        }
      }

      const nextSuspended = !isSuspended;
      const suspensionCache = readSuspensionCache();
      suspensionCache[mover.email] = nextSuspended;

      if (teamMembers && teamMembers.length > 0) {
        teamMembers.forEach((teamMember) => {
          suspensionCache[teamMember.email] = nextSuspended;
        });
      }

      writeSuspensionCache(suspensionCache);

      // Update local state
      setMovers(prevMovers => prevMovers.map(m =>
        m.id === mover.id ? { ...m, status: nextSuspended ? 'suspended' : 'active' } : m
      ));

      // Refresh to get updated ban status
      await fetchMovers();
      setSuspendTarget(null);
    } catch (error) {
      setSuspendError(`Failed to ${action} mover: ${error.message}`);
    } finally {
      setSuspendLoading(false);
    }
  };

  const handleDelete = (moverId) => {
    if (window.confirm('Are you sure you want to delete this mover? This action cannot be undone.')) {
      // Delete from Supabase
      supabase
        .from('movers_data')
        .delete()
        .eq('id', moverId)
        .then(({ error }) => {
          if (error) {
            toast.error('Failed to delete mover');
          } else {
            setMovers(movers.filter(m => m.id !== moverId));
          }
        });
    }
  };

  const handleAddLeads = (mover) => {
    setSelectedMover(mover);
    setShowAddLeadsModal(true);
  };

  const handleUpdateLeads = async (moverId, totalLeads) => {
    try {
      // Update total_leads in Supabase
      const { error } = await supabase
        .from('movers_data')
        .update({ total_leads: totalLeads })
        .eq('id', moverId);

      if (error) throw error;

      // Update local state
      setMovers(movers.map(m =>
        m.id === moverId ? { ...m, total_leads: totalLeads } : m
      ));

      // Show success message
      toast.success('Total leads updated successfully!');
    } catch (error) {
      throw new Error('Failed to update total leads. Please try again.');
    }
  };

  const handleApprove = (mover) => {
    if (window.confirm(`Approve ${mover.name}?`)) {
      setMovers(movers.map(m =>
        m.id === mover.id ? { ...m, status: 'active' } : m
      ));
      setShowDetailsModal(false);
    }
  };

  const handleReject = (mover) => {
    const reason = window.prompt('Reason for rejection:');
    if (reason) {
      setMovers(movers.filter(m => m.id !== mover.id));
      setShowDetailsModal(false);
      toast.error(`${mover.name} has been rejected. Reason: ${reason}`);
    }
  };

  const handleCompanyClick = (update) => {
    // Find mover by name from update or use moverId
    const mover = movers.find(m => m.name === update.moverName || m.id === update.moverId);
    // If not found (e.g., name mismatch), build a minimal profile from the update
    const fallback = {
      id: update.moverId || `temp-${update.id}`,
      name: update.moverName || 'Unknown Mover',
      location: '-',
      status: 'pending',
      plan: 'Not Assigned',
      planExpiry: 'N/A',
      rating: 0,
      leads: 0,
      conversionRate: 0,
      revenue: 0,
      insuranceStatus: update.updateType === 'insurance' ? 'pending' : 'pending',
      insuranceExpiry: update.details?.expiration || 'N/A',
      email: '',
      phone: '',
      website: '',
      address: '',
      businessLicense: update.updateType === 'license' ? update.details?.licenseNo : '',
      insuranceNumber: update.updateType === 'insurance' ? update.details?.policyNumber : '',
      yearsInBusiness: '',
      description: '',
      documents: {}
    };

    setSelectedMover(mover || fallback);
    setShowCompanyModal(true);
  };

  const handleVerifyUpdate = (update) => {
    if (window.confirm(`Verify ${update.updateType} update for ${update.moverName}?`)) {
      // Update mover's insurance/license locally
      setMovers(movers.map(m => {
        if (m.name === update.moverName || m.id === update.moverId) {
          if (update.updateType === 'insurance') {
            return {
              ...m,
              insuranceStatus: 'valid',
              insuranceExpiry: update.details.expiration,
              insuranceNumber: update.details.policyNumber
            };
          } else if (update.updateType === 'license') {
            return {
              ...m,
              businessLicense: update.details.licenseNo
            };
          }
        }
        return m;
      }));
      // Resolve in shared store
      resolveUpdate(update.id, 'verified');
      toast.success(`${update.updateType} verified for ${update.moverName}`);
    }
  };

  const handleRejectUpdate = (update) => {
    const reason = window.prompt('Reason for rejection:');
    if (reason) {
      resolveUpdate(update.id, 'rejected', { reason });
      toast.success(`${update.updateType} update rejected for ${update.moverName}. Reason: ${reason}`);
    }
  };

  const handleSelectMover = (moverId) => {
    setSelectedMovers(prev =>
      prev.includes(moverId)
        ? prev.filter(id => id !== moverId)
        : [...prev, moverId]
    );
  };

  const handleSelectAll = () => {
    if (selectedMovers.length === filteredMovers.length) {
      setSelectedMovers([]);
    } else {
      setSelectedMovers(filteredMovers.map(m => m.id));
    }
  };

  const handleBulkActivate = () => {
    if (window.confirm(`Activate ${selectedMovers.length} selected movers?`)) {
      setMovers(movers.map(m =>
        selectedMovers.includes(m.id) ? { ...m, status: 'active' } : m
      ));
      setSelectedMovers([]);
    }
  };

  const handleBulkSuspend = () => {
    if (window.confirm(`Suspend ${selectedMovers.length} selected movers?`)) {
      setMovers(movers.map(m =>
        selectedMovers.includes(m.id) ? { ...m, status: 'suspended' } : m
      ));
      setSelectedMovers([]);
    }
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Delete ${selectedMovers.length} selected movers? This action cannot be undone.`)) {
      setMovers(movers.filter(m => !selectedMovers.includes(m.id)));
      setSelectedMovers([]);
    }
  };

  const handleExport = (format, fields) => {
    const exportData = prepareExportData(filteredMovers, fields);
    const extensionMap = { csv: 'csv', excel: 'xlsx', json: 'json', pdf: 'pdf' };
    const filename = generateFilename('movers_export', extensionMap[format]);

    switch (format) {
      case 'csv':
        exportToCSV(exportData, filename);
        break;
      case 'excel':
        exportToExcel(exportData, filename);
        break;
      case 'json':
        exportToJSON(exportData, filename);
        break;
      case 'pdf':
        exportToPDF(exportData, filename, 'Mover Management Report');
        break;
      default:
    }
  };

  const exportFields = [
    { key: 'name', label: 'Company Name' },
    { key: 'location', label: 'Location' },
    { key: 'status', label: 'Status' },
    { key: 'plan', label: 'Plan' },
    { key: 'planExpiry', label: 'Plan Expiry' },
    { key: 'rating', label: 'Rating' },
    { key: 'leads', label: 'Leads' },
    { key: 'conversionRate', label: 'Conversion Rate' },
    { key: 'revenue', label: 'Revenue' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'insuranceStatus', label: 'Insurance Status' }
  ];

  return (
    <div>
      <PageHeader
        title="Mover Management"
        subtitle="Review and manage mover accounts"
      />

      {activeTab === 'new' && (
        <>
          {loading ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading movers...</p>
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1">
          <SearchBar
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search movers..."
          />
        </div>
        <div className="flex gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm min-w-[140px]"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>

      {selectedMovers.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">
              {selectedMovers.length} selected
            </span>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={handleBulkActivate}
              className="flex-1 sm:flex-none px-4 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition whitespace-nowrap"
            >
              Bulk Approve
            </button>
            <button
              onClick={handleBulkSuspend}
              className="flex-1 sm:flex-none px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition whitespace-nowrap"
            >
              Bulk Suspend
            </button>
            <button
              onClick={handleBulkDelete}
              className="flex-1 sm:flex-none px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition whitespace-nowrap"
            >
              Bulk Delete
            </button>
          </div>
        </div>
      )}

          <MoverTable
            movers={filteredMovers}
            onViewDetails={handleViewDetails}
            onEdit={handleEdit}
            onSuspend={handleSuspend}
            onAddLeads={handleAddLeads}
            selectedMovers={selectedMovers}
            onSelectMover={handleSelectMover}
            onSelectAll={handleSelectAll}
          />
            </>
          )}
        </>
      )}

      {activeTab === 'updates' && (
        <div className="space-y-4">
          {pendingUpdates.length === 0 ? (
            <div className="bg-white rounded-xl border p-12 text-center">
              <div className="text-gray-400 text-4xl mb-3">📋</div>
              <div className="text-gray-600 font-medium">No pending updates</div>
              <p className="text-sm text-gray-500 mt-1">When movers update their insurance or licenses, they'll appear here for verification.</p>
            </div>
          ) : (
            pendingUpdates.map((update) => {
              // Find mover to get logo
              const mover = movers.find(m => m.name === update.moverName || m.id === update.moverId);
              const CompanyLogo = () => {
                if (mover?.logo) {
                  return <img src={mover.logo} alt={update.moverName} className="w-full h-full object-cover" />;
                }
                return (
                  <div className="w-full h-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-lg font-bold">
                    {update.moverName.charAt(0).toUpperCase()}
                  </div>
                );
              };

              return (
              <div key={update.id} className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {/* Company Logo - Clickable */}
                    <div 
                      className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-orange-500 transition shadow-md"
                      onClick={() => handleCompanyClick(update)}
                      title="View company profile"
                    >
                      <CompanyLogo />
                    </div>
                    <div>
                      <h3 
                        className="font-semibold text-gray-800 text-lg cursor-pointer hover:text-orange-600 transition"
                        onClick={() => handleCompanyClick(update)}
                      >
                        {update.moverName}
                      </h3>
                      <p className="text-sm text-gray-500">{update.submittedAt}</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
                    {update.updateType === 'insurance' ? '🛡️ Insurance' : '📜 License'}
                  </span>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {update.updateType === 'insurance' ? (
                      <>
                        <div>
                          <span className="text-gray-600">Type:</span>
                          <span className="ml-2 font-medium text-gray-800">{update.details.type}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Agency:</span>
                          <span className="ml-2 font-medium text-gray-800">{update.details.agencyName}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Policy #:</span>
                          <span className="ml-2 font-medium text-gray-800">{update.details.policyNumber}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Expiration:</span>
                          <span className="ml-2 font-medium text-gray-800">{update.details.expiration}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Amount:</span>
                          <span className="ml-2 font-medium text-gray-800">${parseInt(update.details.amount).toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Contact:</span>
                          <span className="ml-2 font-medium text-gray-800">{update.details.contact}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <span className="text-gray-600">State:</span>
                          <span className="ml-2 font-medium text-gray-800">{update.details.state}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Type:</span>
                          <span className="ml-2 font-medium text-gray-800">{update.details.type}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">License #:</span>
                          <span className="ml-2 font-medium text-gray-800">{update.details.licenseNo}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Name:</span>
                          <span className="ml-2 font-medium text-gray-800">{update.details.name}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Expiration:</span>
                          <span className="ml-2 font-medium text-gray-800">{update.details.expiration}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => handleVerifyUpdate(update)}
                    className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white rounded-lg font-medium transition shadow-[0_4px_0_0_rgb(22,101,52)] hover:shadow-[0_2px_0_0_rgb(22,101,52)] active:shadow-[0_0px_0_0_rgb(22,101,52)] hover:translate-y-[2px] active:translate-y-[4px] text-sm"
                  >
                    ✓ Verify
                  </button>
                  <button
                    onClick={() => handleRejectUpdate(update)}
                    className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white rounded-lg font-medium transition shadow-[0_4px_0_0_rgb(127,29,29)] hover:shadow-[0_2px_0_0_rgb(127,29,29)] active:shadow-[0_0px_0_0_rgb(127,29,29)] hover:translate-y-[2px] active:translate-y-[4px] text-sm"
                  >
                    ✗ Reject
                  </button>
                </div>
              </div>
              )
            })
          )}
        </div>
      )}

      <MoverDetailsModal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        mover={selectedMover}
        onApprove={handleApprove}
        onReject={handleReject}
      />

      <CompanyInfoModal
        isOpen={showCompanyModal}
        onClose={() => setShowCompanyModal(false)}
        mover={selectedMover}
      />

      <AddLeadsModal
        isOpen={showAddLeadsModal}
        onClose={() => setShowAddLeadsModal(false)}
        mover={selectedMover}
        onUpdateLeads={handleUpdateLeads}
      />

      {showEditModal && selectedMover && (
        <EditMoverModal
          mover={selectedMover}
          onClose={() => {
            setShowEditModal(false);
            setSelectedMover(null);
          }}
          onSave={handleSaveEdit}
        />
      )}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        totalRecords={filteredMovers.length}
        availableFields={exportFields}
      />

      {suspendTarget && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-gray-900/55 backdrop-blur-[2px]"
            onClick={closeSuspendModal}
          />

          <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl">
            <div className="border-b border-gray-100 px-6 py-5">
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-11 w-11 items-center justify-center rounded-xl ${
                    suspendTarget.status === 'suspended'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {suspendTarget.status === 'suspended' ? (
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.415l-7.25 7.25a1 1 0 01-1.415 0l-3-3a1 1 0 011.415-1.415l2.293 2.293 6.543-6.543a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 2a5 5 0 00-5 5v2H4a2 2 0 00-2 2v5a2 2 0 002 2h12a2 2 0 002-2v-5a2 2 0 00-2-2h-1V7a5 5 0 00-5-5zm-3 7V7a3 3 0 116 0v2H7z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {suspendTarget.status === 'suspended' ? 'Reactivate Mover' : 'Suspend Mover'}
                  </h3>
                  <p className="text-sm text-gray-500">Admin confirmation required</p>
                </div>
              </div>
            </div>

            <div className="space-y-4 px-6 py-5">
              <p className="text-sm leading-6 text-gray-700">
                {suspendTarget.status === 'suspended'
                  ? `Reactivate ${suspendTarget.name}? They will be able to log in and receive new leads again.`
                  : `Suspend ${suspendTarget.name}? They will not be able to log in until you reactivate the account.`}
              </p>
              {suspendLoading && (
                <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                  <span>
                    {suspendTarget.status === 'suspended'
                      ? 'Reactivating mover account...'
                      : 'Suspending mover account...'}
                  </span>
                </div>
              )}
              {suspendTarget.status !== 'suspended' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Team members linked to this mover will also be suspended.
                </div>
              )}

              {suspendError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {suspendError}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-4">
              <button
                type="button"
                onClick={closeSuspendModal}
                disabled={suspendLoading}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSuspendAction}
                disabled={suspendLoading}
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-70 ${
                  suspendTarget.status === 'suspended'
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {suspendLoading
                  ? 'Processing...'
                  : suspendTarget.status === 'suspended'
                    ? 'Reactivate Account'
                    : 'Suspend Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MoverManagement;
